import axios, { AxiosError } from 'axios';

export const ACCESS_TOKEN_STORAGE_KEY = 'access_token';
export const REFRESH_TOKEN_STORAGE_KEY = 'refresh_token';

export type LoginRequest = {
  email: string;
  password: string;
};

export type LoginResponse = {
  access: string;
  refresh: string;
  role?: 'admin' | 'editor' | 'user';
};

export type EmailVerificationResponse = {
  detail: string;
};

export type AuthUser = {
  id: number;
  email: string;
  username: string;
  role: 'admin' | 'editor' | 'user';
  first_name: string;
  last_name: string;
};

export type AdminUser = {
  id: number;
  email: string;
  username: string;
  role: 'admin' | 'editor' | 'user';
  first_name: string;
  last_name: string;
  is_active: boolean;
  is_staff: boolean;
  last_login: string | null;
  date_joined: string;
  avatar: string | null;
};

export type ApiListResponse<T> =
  | T[]
  | {
      count?: number;
      next?: string | null;
      previous?: string | null;
      results?: T[];
    };

export type NormalizedListResponse<T> = {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
};

export type PaginationQuery = {
  page?: number;
  page_size?: number;
};

export type DictionaryItem = {
  id: number;
  name: string;
  slug: string;
  created_at?: string;
  updated_at?: string;
};

export type LocationDictionaryItem = DictionaryItem & {
  is_active?: boolean;
  sort_order?: number;
  description?: string;
  icon?: string;
  country?: number;
  region?: number;
  county?: number;
};

export type LocationsDictionaryGroup =
  | 'countries'
  | 'regions'
  | 'counties'
  | 'settlements'
  | 'tags'
  | 'location-categories'
  | 'location-road-types'
  | 'location-difficulties'
  | 'location-best-seasons';

export type RoutesDictionaryGroup = 'route-categories' | 'route-tags' | 'route-difficulties';

export type NewsDictionaryGroup = 'news-categories' | 'news-tags';

export type EventsDictionaryGroup = 'event-types';

export type DictionaryGroup =
  | LocationsDictionaryGroup
  | RoutesDictionaryGroup
  | NewsDictionaryGroup
  | EventsDictionaryGroup;

export type LocationStatus = 'draft' | 'pending_review' | 'approved' | 'rejected' | 'published' | 'archived';

export type LocationFact = {
  id?: number;
  text: string;
  sort_order: number;
};

export type LocationReviewAuthor = {
  id: number;
  name: string;
  avatar: string | null;
};

export type LocationReviewsSummary = {
  average_rating: number;
  reviews_count: number;
  ratings_count: number;
  distribution: Record<string, number>;
};

export type ContentFormat = 'blocks_v1';

export type ContentTagItem = {
  id: number;
  name: string;
  slug: string;
};

export type ContentCategoryItem = {
  id: number;
  name: string;
  slug: string;
};

export type ContentBlock = {
  type: string;
  id: string;
  [key: string]: unknown;
};

const CONTENT_BLOCK_STRING_KEYS = new Set([
  'type',
  'id',
  'text',
  'align',
  'anchor',
  'style',
  'variant',
  'title',
  'icon',
  'url',
  'alt',
  'caption',
  'credit',
  'layout',
  'provider',
  'thumbnail',
  'source',
  'author',
  'button_text',
  'button_url',
  'language',
  'content',
  'name',
  'slug',
  'href',
]);

const sanitizeContentBlockValue = (value: unknown, parentKey?: string): unknown => {
  if (Array.isArray(value)) {
    return value.map((item) => sanitizeContentBlockValue(item, parentKey));
  }
  if (value && typeof value === 'object') {
    const source = value as Record<string, unknown>;
    const target: Record<string, unknown> = {};
    Object.entries(source).forEach(([key, current]) => {
      if (key === 'id') {
        // Block id must be string, but tag-item id must be integer.
        if (typeof source.type === 'string') {
          target[key] = typeof current === 'string' ? current : current == null ? '' : String(current);
        } else if (typeof current === 'number') {
          target[key] = Number.isFinite(current) ? Math.trunc(current) : 0;
        } else if (typeof current === 'string') {
          const n = Number(current);
          target[key] = Number.isFinite(n) ? Math.trunc(n) : 0;
        } else {
          target[key] = 0;
        }
        return;
      }
      target[key] = sanitizeContentBlockValue(current, key);
    });
    return target;
  }
  if (CONTENT_BLOCK_STRING_KEYS.has(String(parentKey || ''))) {
    return typeof value === 'string' ? value : value == null ? '' : String(value);
  }
  return value;
};

const sanitizeContentBlocksInPayload = <T extends Record<string, unknown>>(payload: T): T => {
  if (!Array.isArray(payload.content_blocks)) return payload;
  const sanitized = payload.content_blocks.map((block) => sanitizeContentBlockValue(block) as ContentBlock);
  return { ...payload, content_blocks: sanitized };
};

export type LocationItem = {
  id: number;
  title: string;
  slug: string;
  description: string;
  content_format?: ContentFormat;
  content_blocks?: ContentBlock[];
  content_html_sanitized?: string;
  cover_image?: string | null;
  location_name: string;
  latitude: string;
  longitude: string;
  country: number | null;
  region: number | null;
  county: number | null;
  settlement: number | null;
  difficulty: number | null;
  road_type: number | null;
  best_season: number | null;
  difficulty_display?: string;
  road_type_display?: string;
  best_season_display?: string;
  safety_notes: string;
  access_rules: string;
  status: LocationStatus;
  source: string;
  review_comment: string;
  published_at: string | null;
  submitter_username?: string;
  submitter_display?: string;
  is_archived: boolean;
  is_featured: boolean;
  rating_avg: string;
  rating_count: number;
  reviews_count: number;
  favorites_count: number;
  is_favorited: boolean;
  reviews_preview?: LocationReview[];
  reviews_summary?: LocationReviewsSummary;
  facts: LocationFact[];
  tag_ids: number[];
  category_ids: number[];
  created_at: string;
  updated_at: string;
};

export type CreateLocationPayload = {
  title: string;
  slug?: string;
  description: string;
  content_format?: ContentFormat;
  content_blocks?: ContentBlock[];
  latitude: string;
  longitude: string;
  country?: number;
  region?: number;
  county?: number;
  settlement?: number;
  difficulty?: number;
  road_type?: number;
  best_season?: number;
  safety_notes?: string;
  access_rules?: string;
  is_featured?: boolean;
  tag_ids?: number[];
  category_ids?: number[];
  facts?: LocationFact[];
};

export type UpdateLocationPayload = Partial<CreateLocationPayload> & {
  title?: string;
  description?: string;
  latitude?: string;
  longitude?: string;
};

export type LocationReview = {
  id: number;
  message: string;
  rating_value: number | null;
  status: 'pending' | 'pending_review' | 'approved' | 'rejected';
  created_at: string;
  updated_at: string;
  author: LocationReviewAuthor;
};

export type LocationReviewListResponse = NormalizedListResponse<LocationReview> & {
  summary: LocationReviewsSummary;
};

export type LocationRatingSummaryResponse = LocationReviewsSummary & {
  my_rating: number | null;
};

export type LocationMyRating = {
  value: number;
};

export type LocationFavorite = {
  id: number;
  location: number;
  user: number;
  created_at: string;
};

export type LocationMedia = {
  id: number;
  location: number;
  kind: 'image' | 'video';
  file_url: string;
  file_large_url?: string | null;
  file_thumbnail_url?: string | null;
  title: string;
  sort_order: number;
  created_at: string;
};

export type AiTask = {
  id: number;
  kind: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  prompt: string;
  input_payload: Record<string, unknown>;
  output_payload: Record<string, unknown> | null;
  error_message: string | null;
  proposals_count: number;
  started_at: string | null;
  completed_at: string | null;
  created_at: string;
  updated_at: string;
};

export type AiProposal = {
  id: number;
  task_id: number;
  proposal_type: 'location_create' | 'location_patch' | 'route_create' | 'route_patch';
  status: 'draft' | 'approved' | 'rejected' | 'applied';
  title: string;
  summary: string;
  payload: Record<string, unknown>;
  target_model: string;
  target_id: number | null;
  review_comment: string;
  reviewed_at: string | null;
  applied_at: string | null;
  created_at: string;
  updated_at: string;
};

export type EventTypeItem = {
  id: number;
  name: string;
  slug: string;
  description: string;
  is_active: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
};

export type RouteStatus = 'draft' | 'pending_review' | 'approved' | 'published' | 'rejected' | 'archived';
export type RouteStopType = 'start' | 'via' | 'finish' | 'poi' | 'fuel' | 'food' | 'rest';

export type RouteWaypointItem = {
  id?: number;
  sort_order: number;
  location?: number | null;
  latitude?: string | null;
  longitude?: string | null;
  title?: string;
  location_name?: string;
  stop_type?: RouteStopType;
  note?: string;
};

export type RouteItem = {
  id: number;
  title: string;
  slug: string;
  location_name: string;
  distance_km: string;
  duration_minutes: number;
  description: string;
  content_format?: ContentFormat;
  content_blocks?: ContentBlock[];
  content_html_sanitized?: string;
  elevation_gain_m: number;
  elevation_loss_m: number;
  difficulty?: number | null;
  cover_image: string | null;
  is_featured: boolean;
  status: RouteStatus;
  source: 'admin' | 'editor' | 'user';
  review_comment: string;
  reviewed_at: string | null;
  published_at: string | null;
  start_latitude: string | null;
  start_longitude: string | null;
  end_latitude: string | null;
  end_longitude: string | null;
  waypoints_count: number;
  favorites_count: number;
  reviews_count: number;
  rating_avg: string;
  rating_count: number;
  reviews_preview?: RouteReview[];
  reviews_summary?: RouteReviewsSummary;
  is_favorited: boolean;
  waypoints: RouteWaypointItem[];
  category_ids: number[];
  tag_ids: number[];
  created_at: string;
  updated_at: string;
};

export type RouteReviewAuthor = {
  id: number;
  name: string;
  avatar: string | null;
};

export type RouteReviewsSummary = {
  average_rating: number;
  reviews_count: number;
  ratings_count: number;
  distribution: Record<'5' | '4' | '3' | '2' | '1', number>;
};

export type RouteReviewListResponse = NormalizedListResponse<RouteReview> & {
  summary: RouteReviewsSummary;
};

export type RouteRatingSummaryResponse = {
  average_rating: number;
  reviews_count: number;
  ratings_count: number;
  distribution: Record<'5' | '4' | '3' | '2' | '1', number>;
  my_rating: number | null;
};

export type RouteMyRating = {
  value: number;
};

export type RouteMediaItem = {
  id: number;
  route: number;
  kind: 'image';
  file: string;
  file_url: string;
  file_large?: string | null;
  file_large_url?: string | null;
  file_thumbnail?: string | null;
  file_thumbnail_url?: string | null;
  title: string;
  sort_order: number;
  created_at: string;
};

export type EventStatus = 'draft' | 'pending_review' | 'published' | 'rejected' | 'cancelled' | 'finished';
export type EventOriginType = 'official' | 'community';

export type EventItem = {
  id: number;
  origin_type: EventOriginType;
  event_type: number | null;
  event_type_name?: string;
  title: string;
  slug: string;
  description: string;
  content_format?: ContentFormat;
  content_blocks?: ContentBlock[];
  content_html_sanitized?: string;
  starts_at: string;
  ends_at: string | null;
  timezone: string;
  country: number | null;
  region: number | null;
  county: number | null;
  settlement: number | null;
  location_name: string;
  latitude: string | null;
  longitude: string | null;
  meeting_point_note: string;
  capacity: number | null;
  is_free: boolean;
  price_from: string | null;
  cover_image: string | null;
  is_featured: boolean;
  status: EventStatus;
  moderation_comment: string;
  host_user: number | null;
  host_name: string | null;
  published_at: string | null;
  cancelled_at: string | null;
  finished_at: string | null;
  rsvp_going_count: number;
  rsvp_interested_count: number;
  rsvp_not_going_count: number;
  messages_count: number;
  my_rsvp: null | {
    status: 'going' | 'interested' | 'not_going';
    guests_count: number;
    comment: string;
  };
  created_at: string;
  updated_at: string;
};

export type EventMediaItem = {
  id: number;
  event: number;
  kind: 'image';
  file: string;
  file_url: string;
  title: string;
  sort_order: number;
  created_at: string;
};

export type EventMessageItem = {
  id: number;
  event: number;
  author: number;
  author_email: string;
  author_name: string;
  message: string;
  status: 'active' | 'hidden' | 'deleted_by_author' | 'deleted_by_moderation';
  reply_to: number | null;
  edited_at: string | null;
  created_at: string;
  updated_at: string;
};

export type DashboardModerationSummary = {
  pending_locations: number;
  pending_routes: number;
  total_queue: number;
  rejected_locations: number;
  messages_total: number;
  messages_location_reviews: number;
  messages_route_reviews: number;
  messages_news_comments: number;
  messages_event_messages: number;
  recent_pending_locations: Array<{
    id: number;
    title: string;
    source: string;
    created_at: string;
  }>;
  recent_pending_routes: Array<{
    id: number;
    title: string;
    created_at: string;
  }>;
};

export type DashboardMessageStreamItem = {
  id: number;
  module: 'location_review' | 'route_review' | 'news_review' | 'event_review';
  parent_id: number;
  parent_title: string;
  author_id: number | null;
  author_name: string;
  author_is_blocked: boolean;
  text: string;
  status: string;
  created_at: string;
  updated_at: string;
};

export type NewsStatus = 'draft' | 'pending_review' | 'published' | 'archived';

export type NewsItem = {
  id: number;
  title: string;
  slug: string;
  excerpt: string;
  description: string;
  content: string;
  category: number;
  category_detail?: ContentCategoryItem | null;
  tag_ids: number[];
  tags?: ContentTagItem[];
  content_format?: ContentFormat;
  content_blocks?: ContentBlock[];
  content_html_sanitized?: string;
  external_url: string;
  source_name: string;
  source_url: string;
  cover_image: string | null;
  is_featured: boolean;
  is_pinned: boolean;
  status: NewsStatus;
  published_at: string | null;
  review_comment: string;
  rating_avg: string;
  rating_count: number;
  comments_count: number;
  bookmarks_count: number;
  view_count: number;
  reading_time_minutes: number;
  seo_title: string;
  seo_description: string;
  is_bookmarked: boolean;
  created_at: string;
  updated_at: string;
};

export type NewsMediaItem = {
  id: number;
  news: number;
  kind: 'image';
  file: string;
  file_url: string;
  title: string;
  sort_order: number;
  created_at: string;
};

export type NewsCommentItem = {
  id: number;
  news: number;
  news_title?: string;
  user: number;
  author_name?: string;
  author?: {
    id: number;
    name: string;
    avatar: string | null;
  };
  message: string;
  status: 'pending' | 'approved' | 'rejected';
  created_at: string;
  updated_at: string;
};

export type RouteReview = {
  id: number;
  message: string;
  rating_value: number | null;
  status: 'pending' | 'approved' | 'rejected';
  created_at: string;
  updated_at: string;
  author: RouteReviewAuthor;
};

export const apiClient = axios.create({
  baseURL: '/api',
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: false,
});

apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem(ACCESS_TOKEN_STORAGE_KEY);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem(ACCESS_TOKEN_STORAGE_KEY);
      localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
    }
    return Promise.reject(error);
  }
);

export const login = async (payload: LoginRequest): Promise<LoginResponse> => {
  const response = await apiClient.post<LoginResponse>('/v1/auth/login/', payload);
  return response.data;
};

export const verifyEmail = async (token: string): Promise<EmailVerificationResponse> => {
  const response = await apiClient.post<EmailVerificationResponse>('/v1/auth/email-verify/', { token });
  return response.data;
};

export const me = async (): Promise<AuthUser> => {
  const response = await apiClient.get<AuthUser>('/v1/auth/me/');
  return response.data;
};

export const listAdminUsers = async (params?: {
  search?: string;
  role?: 'admin' | 'editor' | 'user' | 'all';
  is_active?: 'true' | 'false' | 'all';
  ordering?: string;
  page?: number;
  page_size?: number;
}): Promise<NormalizedListResponse<AdminUser>> => {
  const query: Record<string, string | number> = {};
  if (params?.search) query.search = params.search;
  if (params?.role && params.role !== 'all') query.role = params.role;
  if (params?.is_active && params.is_active !== 'all') query.is_active = params.is_active;
  if (params?.ordering) query.ordering = params.ordering;
  if (params?.page) query.page = params.page;
  if (params?.page_size) query.page_size = params.page_size;

  const response = await apiClient.get<ApiListResponse<AdminUser>>('/v1/auth/users/', { params: query });
  return normalizeListResponse(response.data);
};

export const getAdminUser = async (id: number): Promise<AdminUser> => {
  const response = await apiClient.get<AdminUser>(`/v1/auth/users/${id}/`);
  return response.data;
};

export const createAdminUser = async (payload: {
  email: string;
  username?: string;
  password: string;
  first_name?: string;
  last_name?: string;
  role?: 'admin' | 'editor' | 'user';
  is_active?: boolean;
  avatar?: File | null;
}): Promise<AdminUser> => {
  const formData = new FormData();
  formData.set('email', payload.email);
  if (payload.username) formData.set('username', payload.username);
  formData.set('password', payload.password);
  if (payload.first_name !== undefined) formData.set('first_name', payload.first_name);
  if (payload.last_name !== undefined) formData.set('last_name', payload.last_name);
  if (payload.role) formData.set('role', payload.role);
  if (payload.is_active !== undefined) formData.set('is_active', String(payload.is_active));
  if (payload.avatar) formData.set('avatar', payload.avatar);

  const response = await apiClient.post<AdminUser>('/v1/auth/users/', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

export const patchAdminUser = async (
  id: number,
  payload: Partial<{
    email: string;
    username: string;
    password: string;
    first_name: string;
    last_name: string;
    role: 'admin' | 'editor' | 'user';
    is_active: boolean;
    avatar: File | null;
  }>
): Promise<AdminUser> => {
  const formData = new FormData();
  const entries = Object.entries(payload) as Array<[string, unknown]>;
  for (const [key, value] of entries) {
    if (value === undefined) continue;
    if (key === 'avatar') {
      if (value instanceof File) formData.set('avatar', value);
      continue;
    }
    formData.set(key, String(value));
  }

  const response = await apiClient.patch<AdminUser>(`/v1/auth/users/${id}/`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

export const deleteAdminUser = async (id: number): Promise<void> => {
  await apiClient.delete(`/v1/auth/users/${id}/`);
};

export const logout = async (refresh: string): Promise<void> => {
  await apiClient.post('/v1/auth/logout/', { refresh });
};

export const healthCheck = async () => {
  const response = await apiClient.get('/health/');
  return response.data;
};

export const normalizeListResponse = <T>(payload: ApiListResponse<T>): NormalizedListResponse<T> => {
  if (Array.isArray(payload)) {
    return {
      count: payload.length,
      next: null,
      previous: null,
      results: payload,
    };
  }

  return {
    count: payload.count ?? (payload.results?.length ?? 0),
    next: payload.next ?? null,
    previous: payload.previous ?? null,
    results: payload.results ?? [],
  };
};

const createEmptyLocationReviewsSummary = (): LocationReviewsSummary => ({
  average_rating: 0,
  reviews_count: 0,
  ratings_count: 0,
  distribution: {
    '5': 0,
    '4': 0,
    '3': 0,
    '2': 0,
    '1': 0,
  },
});

const createEmptyRouteReviewsSummary = (): RouteReviewsSummary => ({
  average_rating: 0,
  reviews_count: 0,
  ratings_count: 0,
  distribution: {
    '5': 0,
    '4': 0,
    '3': 0,
    '2': 0,
    '1': 0,
  },
});

const BULK_PAGE_SIZE = 100;
const BULK_MAX_PAGES = 100;

const collectAllPages = async <T>(
  loadPage: (params: PaginationQuery) => Promise<NormalizedListResponse<T>>
): Promise<NormalizedListResponse<T>> => {
  let page = 1;
  let pagesRead = 0;
  let count = 0;
  let previous: string | null = null;
  let next: string | null = null;
  const results: T[] = [];

  while (page <= BULK_MAX_PAGES) {
    const payload = await loadPage({ page, page_size: BULK_PAGE_SIZE });
    if (page === 1) {
      count = payload.count;
      previous = payload.previous;
    }
    results.push(...payload.results);
    next = payload.next;
    pagesRead += 1;
    if (!payload.next || payload.results.length === 0) break;
    page += 1;
  }

  if (pagesRead === BULK_MAX_PAGES && next) {
    // Safety break for malformed pagination responses.
    next = null;
  }

  return {
    count: Math.max(count, results.length),
    next,
    previous,
    results,
  };
};

const extractValidationMessage = (data: unknown): string | undefined => {
  if (!data || typeof data !== 'object') return undefined;
  const payload = data as Record<string, unknown>;
  for (const [field, value] of Object.entries(payload)) {
    if (field === 'error' || field === 'detail') continue;
    if (Array.isArray(value) && value.length > 0) {
      const first = value[0];
      if (typeof first === 'string') return `${field}: ${first}`;
      if (first && typeof first === 'object') {
        const nested = extractValidationMessage(first);
        if (nested) return `${field}: ${nested}`;
      }
    }
    if (value && typeof value === 'object') {
      const nested = extractValidationMessage(value);
      if (nested) return `${field}: ${nested}`;
    }
    if (typeof value === 'string' && value.trim()) return `${field}: ${value}`;
  }
  return undefined;
};

export const getApiErrorMessage = (error: unknown, fallback: string): string => {
  const axiosError = error as AxiosError<{ error?: { message?: string }; detail?: string }>;
  return (
    axiosError.response?.data?.error?.message ||
    axiosError.response?.data?.detail ||
    extractValidationMessage(axiosError.response?.data) ||
    (error instanceof Error ? error.message : undefined) ||
    fallback
  );
};

export const listLocations = async (params?: Record<string, string | number | boolean | undefined>) => {
  const response = await apiClient.get<ApiListResponse<LocationItem>>('/v1/locations/', { params });
  return normalizeListResponse(response.data);
};

export const getLocation = async (id: number): Promise<LocationItem> => {
  const response = await apiClient.get<LocationItem>(`/v1/locations/${id}/`);
  return response.data;
};

export const createLocation = async (payload: CreateLocationPayload): Promise<LocationItem> => {
  const response = await apiClient.post<LocationItem>('/v1/locations/', sanitizeContentBlocksInPayload(payload as Record<string, unknown>));
  return response.data;
};

export const importLocationFromJson = async (payload: Record<string, unknown>): Promise<LocationItem> => {
  const response = await apiClient.post<LocationItem>('/v1/locations/import-json/', payload);
  return response.data;
};

export const importLocationIntoExistingFromJson = async (id: number, payload: Record<string, unknown>): Promise<LocationItem> => {
  const response = await apiClient.post<LocationItem>(`/v1/locations/${id}/import-json/`, payload);
  return response.data;
};

export const suggestLocation = async (payload: CreateLocationPayload): Promise<LocationItem> => {
  const response = await apiClient.post<LocationItem>('/v1/locations/suggest/', payload);
  return response.data;
};

export const publishLocation = async (id: number): Promise<LocationItem> => {
  const response = await apiClient.post<LocationItem>(`/v1/locations/${id}/publish/`, {});
  return response.data;
};

export const patchLocation = async (id: number, payload: UpdateLocationPayload): Promise<LocationItem> => {
  const response = await apiClient.patch<LocationItem>(
    `/v1/locations/${id}/`,
    sanitizeContentBlocksInPayload(payload as Record<string, unknown>)
  );
  return response.data;
};

export const deleteLocation = async (id: number): Promise<void> => {
  await apiClient.delete(`/v1/locations/${id}/`);
};

export const listMyLocationSuggestions = async (
  params?: PaginationQuery
): Promise<NormalizedListResponse<LocationItem>> => {
  const response = await apiClient.get<ApiListResponse<LocationItem>>('/v1/locations/my-suggestions/', { params });
  return normalizeListResponse(response.data);
};

export const listLocationModerationQueue = async (
  params?: PaginationQuery
): Promise<NormalizedListResponse<LocationItem>> => {
  const response = await apiClient.get<ApiListResponse<LocationItem>>('/v1/locations/moderation/queue/', { params });
  return normalizeListResponse(response.data);
};

export const approveLocation = async (id: number, comment = ''): Promise<LocationItem> => {
  const response = await apiClient.post<LocationItem>(`/v1/locations/${id}/approve/`, { comment });
  return response.data;
};

export const rejectLocation = async (id: number, comment = ''): Promise<LocationItem> => {
  const response = await apiClient.post<LocationItem>(`/v1/locations/${id}/reject/`, { comment });
  return response.data;
};

export const resubmitLocation = async (id: number, payload: UpdateLocationPayload): Promise<LocationItem> => {
  const response = await apiClient.patch<LocationItem>(`/v1/locations/${id}/resubmit/`, payload);
  return response.data;
};

export const listLocationReviews = async (
  id: number,
  params?: PaginationQuery
): Promise<LocationReviewListResponse> => {
  const response = await apiClient.get<
    | ApiListResponse<LocationReview>
    | ({ summary?: LocationReviewsSummary } & {
        count?: number;
        next?: string | null;
        previous?: string | null;
        results?: LocationReview[];
      })
  >(`/v1/locations/${id}/reviews/`, { params });

  const payload = response.data;
  const normalized = normalizeListResponse<LocationReview>(payload as ApiListResponse<LocationReview>);
  const summary =
    Array.isArray(payload) || !('summary' in payload)
      ? createEmptyLocationReviewsSummary()
      : payload.summary ?? createEmptyLocationReviewsSummary();
  return {
    ...normalized,
    summary,
  };
};

export const listAllLocationReviews = async (id: number): Promise<LocationReviewListResponse> => {
  let page = 1;
  let pagesRead = 0;
  let count = 0;
  let previous: string | null = null;
  let next: string | null = null;
  let summary = createEmptyLocationReviewsSummary();
  const results: LocationReview[] = [];

  while (page <= BULK_MAX_PAGES) {
    const payload = await listLocationReviews(id, { page, page_size: BULK_PAGE_SIZE });
    if (page === 1) {
      count = payload.count;
      previous = payload.previous;
      summary = payload.summary;
    }
    results.push(...payload.results);
    next = payload.next;
    pagesRead += 1;
    if (!payload.next || payload.results.length === 0) break;
    page += 1;
  }

  if (pagesRead === BULK_MAX_PAGES && next) {
    next = null;
  }

  return {
    count: Math.max(count, results.length),
    next,
    previous,
    results,
    summary,
  };
};

export const createLocationReview = async (
  id: number,
  payload: { message: string; rating_value: number }
): Promise<LocationReview> => {
  const response = await apiClient.post<LocationReview>(`/v1/locations/${id}/reviews/`, payload);
  return response.data;
};

export const deleteLocationReview = async (locationId: number, reviewId: number): Promise<void> => {
  await apiClient.delete(`/v1/locations/${locationId}/reviews/${reviewId}/`);
};

export const approveLocationReview = async (locationId: number, reviewId: number): Promise<LocationReview> => {
  const response = await apiClient.post<LocationReview>(
    `/v1/locations/${locationId}/reviews/${reviewId}/approve/`,
    {}
  );
  return response.data;
};

export const rejectLocationReview = async (
  locationId: number,
  reviewId: number,
  comment = ''
): Promise<LocationReview> => {
  const response = await apiClient.post<LocationReview>(
    `/v1/locations/${locationId}/reviews/${reviewId}/reject/`,
    { comment }
  );
  return response.data;
};

export const rateLocation = async (id: number, value: number): Promise<LocationRatingSummaryResponse> => {
  const response = await apiClient.post<LocationRatingSummaryResponse>(`/v1/locations/${id}/ratings/`, { value });
  return response.data;
};

export const getMyLocationRating = async (id: number): Promise<LocationMyRating | null> => {
  try {
    const response = await apiClient.get<LocationMyRating>(`/v1/locations/${id}/ratings/my/`);
    return response.data;
  } catch (error) {
    const axiosError = error as AxiosError;
    if (axiosError.response?.status === 204) {
      return null;
    }
    throw error;
  }
};

export const addLocationFavorite = async (id: number): Promise<LocationFavorite> => {
  const response = await apiClient.post<LocationFavorite>(`/v1/locations/${id}/favorite/`, {});
  return response.data;
};

export const removeLocationFavorite = async (id: number): Promise<void> => {
  await apiClient.delete(`/v1/locations/${id}/favorite/`);
};

export const uploadLocationMedia = async (id: number, payload: FormData): Promise<LocationMedia> => {
  const response = await apiClient.post<LocationMedia>(`/v1/locations/${id}/media/`, payload, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

export const setLocationCover = async (
  id: number,
  payload: { coverImage: File } | { mediaId: number }
): Promise<LocationItem> => {
  const formData = new FormData();
  if ('coverImage' in payload) {
    formData.append('cover_image', payload.coverImage);
  } else {
    formData.append('media_id', String(payload.mediaId));
  }
  const response = await apiClient.patch<LocationItem>(`/v1/locations/${id}/cover/`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

export const listLocationMedia = async (
  id: number,
  params?: PaginationQuery
): Promise<NormalizedListResponse<LocationMedia>> => {
  const response = await apiClient.get<ApiListResponse<LocationMedia>>(`/v1/locations/${id}/media/`, { params });
  return normalizeListResponse(response.data);
};

export const listAllLocationMedia = async (id: number): Promise<NormalizedListResponse<LocationMedia>> =>
  collectAllPages<LocationMedia>((params) => listLocationMedia(id, params));

export const deleteLocationMedia = async (locationId: number, mediaId: number): Promise<void> => {
  await apiClient.delete(`/v1/locations/${locationId}/media/${mediaId}/`);
};

export const reorderLocationMedia = async (
  locationId: number,
  items: Array<{ id: number; sort_order: number }>
): Promise<LocationMedia[]> => {
  const response = await apiClient.patch<LocationMedia[]>(`/v1/locations/${locationId}/media/reorder/`, { items });
  return response.data;
};

export const listCountries = async (): Promise<DictionaryItem[]> => {
  const response = await apiClient.get<ApiListResponse<DictionaryItem>>('/v1/countries/');
  return normalizeListResponse(response.data).results;
};

export const listRegions = async (): Promise<Array<DictionaryItem & { country: number }>> => {
  const response = await apiClient.get<ApiListResponse<DictionaryItem & { country: number }>>('/v1/regions/');
  return normalizeListResponse(response.data).results;
};

export const listCounties = async (): Promise<Array<DictionaryItem & { region: number }>> => {
  const response = await apiClient.get<ApiListResponse<DictionaryItem & { region: number }>>('/v1/counties/');
  return normalizeListResponse(response.data).results;
};

export const listSettlements = async (): Promise<Array<DictionaryItem & { county: number }>> => {
  const response = await apiClient.get<ApiListResponse<DictionaryItem & { county: number }>>('/v1/settlements/');
  return normalizeListResponse(response.data).results;
};

export const listCategories = async (): Promise<DictionaryItem[]> => {
  const response = await apiClient.get<ApiListResponse<DictionaryItem>>('/v1/location-categories/');
  return normalizeListResponse(response.data).results;
};

export const listTags = async (): Promise<DictionaryItem[]> => {
  const response = await apiClient.get<ApiListResponse<DictionaryItem>>('/v1/tags/');
  return normalizeListResponse(response.data).results;
};

export const listLocationRoadTypes = async (): Promise<LocationDictionaryItem[]> => {
  const response = await apiClient.get<ApiListResponse<LocationDictionaryItem>>('/v1/location-road-types/');
  return normalizeListResponse(response.data).results;
};

export const listLocationDifficulties = async (): Promise<LocationDictionaryItem[]> => {
  const response = await apiClient.get<ApiListResponse<LocationDictionaryItem>>('/v1/location-difficulties/');
  return normalizeListResponse(response.data).results;
};

export const listLocationBestSeasons = async (): Promise<LocationDictionaryItem[]> => {
  const response = await apiClient.get<ApiListResponse<LocationDictionaryItem>>('/v1/location-best-seasons/');
  return normalizeListResponse(response.data).results;
};

export const listRouteCategories = async (): Promise<LocationDictionaryItem[]> => {
  const response = await apiClient.get<ApiListResponse<LocationDictionaryItem>>('/v1/route-categories/');
  return normalizeListResponse(response.data).results;
};

export const listRouteTags = async (): Promise<LocationDictionaryItem[]> => {
  const response = await apiClient.get<ApiListResponse<LocationDictionaryItem>>('/v1/route-tags/');
  return normalizeListResponse(response.data).results;
};

export const listRouteDifficulties = async (): Promise<LocationDictionaryItem[]> => {
  const response = await apiClient.get<ApiListResponse<LocationDictionaryItem>>('/v1/route-difficulties/');
  return normalizeListResponse(response.data).results;
};

export const listNewsCategories = async (): Promise<LocationDictionaryItem[]> => {
  const response = await apiClient.get<ApiListResponse<LocationDictionaryItem>>('/v1/news-categories/');
  return normalizeListResponse(response.data).results;
};

export const listNewsTags = async (): Promise<LocationDictionaryItem[]> => {
  const response = await apiClient.get<ApiListResponse<LocationDictionaryItem>>('/v1/news-tags/');
  return normalizeListResponse(response.data).results;
};

const LOCATIONS_DICTIONARY_ENDPOINTS: Record<LocationsDictionaryGroup, string> = {
  countries: '/v1/countries/',
  regions: '/v1/regions/',
  counties: '/v1/counties/',
  settlements: '/v1/settlements/',
  tags: '/v1/tags/',
  'location-categories': '/v1/location-categories/',
  'location-road-types': '/v1/location-road-types/',
  'location-difficulties': '/v1/location-difficulties/',
  'location-best-seasons': '/v1/location-best-seasons/',
};

const DICTIONARY_ENDPOINTS: Record<DictionaryGroup, string> = {
  ...LOCATIONS_DICTIONARY_ENDPOINTS,
  'route-categories': '/v1/route-categories/',
  'route-tags': '/v1/route-tags/',
  'route-difficulties': '/v1/route-difficulties/',
  'news-categories': '/v1/news-categories/',
  'news-tags': '/v1/news-tags/',
  'event-types': '/v1/event-types/',
};

export const getDictionaryItem = async (group: DictionaryGroup, id: number): Promise<LocationDictionaryItem> => {
  const response = await apiClient.get<LocationDictionaryItem>(`${DICTIONARY_ENDPOINTS[group]}${id}/`);
  return response.data;
};

export const patchDictionaryItem = async (
  group: DictionaryGroup,
  id: number,
  payload: Record<string, unknown>
): Promise<LocationDictionaryItem> => {
  const response = await apiClient.patch<LocationDictionaryItem>(`${DICTIONARY_ENDPOINTS[group]}${id}/`, payload);
  return response.data;
};

export const deleteDictionaryItem = async (group: DictionaryGroup, id: number): Promise<void> => {
  await apiClient.delete(`${DICTIONARY_ENDPOINTS[group]}${id}/`);
};

export const getLocationDictionaryItem = async (
  group: LocationsDictionaryGroup,
  id: number
): Promise<LocationDictionaryItem> => {
  const response = await apiClient.get<LocationDictionaryItem>(`${LOCATIONS_DICTIONARY_ENDPOINTS[group]}${id}/`);
  return response.data;
};

export const patchLocationDictionaryItem = async (
  group: LocationsDictionaryGroup,
  id: number,
  payload: Record<string, unknown>
): Promise<LocationDictionaryItem> => {
  const response = await apiClient.patch<LocationDictionaryItem>(
    `${LOCATIONS_DICTIONARY_ENDPOINTS[group]}${id}/`,
    payload
  );
  return response.data;
};

export const deleteLocationDictionaryItem = async (group: LocationsDictionaryGroup, id: number): Promise<void> => {
  await apiClient.delete(`${LOCATIONS_DICTIONARY_ENDPOINTS[group]}${id}/`);
};

export const createCountry = async (payload: {
  name: string;
  slug: string;
  is_active?: boolean;
  sort_order?: number;
}): Promise<LocationDictionaryItem> => {
  const response = await apiClient.post<LocationDictionaryItem>('/v1/countries/', payload);
  return response.data;
};

export const createRegion = async (payload: {
  name: string;
  slug: string;
  country: number;
  is_active?: boolean;
  sort_order?: number;
}): Promise<LocationDictionaryItem> => {
  const response = await apiClient.post<LocationDictionaryItem>('/v1/regions/', payload);
  return response.data;
};

export const createCounty = async (payload: {
  name: string;
  slug: string;
  region: number;
  is_active?: boolean;
  sort_order?: number;
}): Promise<LocationDictionaryItem> => {
  const response = await apiClient.post<LocationDictionaryItem>('/v1/counties/', payload);
  return response.data;
};

export const createSettlement = async (payload: {
  name: string;
  slug: string;
  county: number;
  is_active?: boolean;
  sort_order?: number;
}): Promise<LocationDictionaryItem> => {
  const response = await apiClient.post<LocationDictionaryItem>('/v1/settlements/', payload);
  return response.data;
};

export const createTag = async (payload: {
  name: string;
  slug: string;
  is_active?: boolean;
  sort_order?: number;
}): Promise<LocationDictionaryItem> => {
  const response = await apiClient.post<LocationDictionaryItem>('/v1/tags/', payload);
  return response.data;
};

export const createLocationCategory = async (payload: {
  name: string;
  slug: string;
  description?: string;
  icon?: string;
  is_active?: boolean;
  sort_order?: number;
}): Promise<LocationDictionaryItem> => {
  const response = await apiClient.post<LocationDictionaryItem>('/v1/location-categories/', payload);
  return response.data;
};

export const createLocationRoadType = async (payload: {
  name: string;
  slug: string;
  is_active?: boolean;
  sort_order?: number;
}): Promise<LocationDictionaryItem> => {
  const response = await apiClient.post<LocationDictionaryItem>('/v1/location-road-types/', payload);
  return response.data;
};

export const createLocationDifficulty = async (payload: {
  name: string;
  slug: string;
  is_active?: boolean;
  sort_order?: number;
}): Promise<LocationDictionaryItem> => {
  const response = await apiClient.post<LocationDictionaryItem>('/v1/location-difficulties/', payload);
  return response.data;
};

export const createLocationBestSeason = async (payload: {
  name: string;
  slug: string;
  is_active?: boolean;
  sort_order?: number;
}): Promise<LocationDictionaryItem> => {
  const response = await apiClient.post<LocationDictionaryItem>('/v1/location-best-seasons/', payload);
  return response.data;
};

export const createRouteCategory = async (payload: {
  name: string;
  slug: string;
  is_active?: boolean;
  sort_order?: number;
}): Promise<LocationDictionaryItem> => {
  const response = await apiClient.post<LocationDictionaryItem>('/v1/route-categories/', payload);
  return response.data;
};

export const createRouteTag = async (payload: {
  name: string;
  slug: string;
  is_active?: boolean;
  sort_order?: number;
}): Promise<LocationDictionaryItem> => {
  const response = await apiClient.post<LocationDictionaryItem>('/v1/route-tags/', payload);
  return response.data;
};

export const createRouteDifficulty = async (payload: {
  name: string;
  slug: string;
  is_active?: boolean;
  sort_order?: number;
}): Promise<LocationDictionaryItem> => {
  const response = await apiClient.post<LocationDictionaryItem>('/v1/route-difficulties/', payload);
  return response.data;
};

export const createNewsCategory = async (payload: {
  name: string;
  slug: string;
  is_active?: boolean;
  sort_order?: number;
}): Promise<LocationDictionaryItem> => {
  const response = await apiClient.post<LocationDictionaryItem>('/v1/news-categories/', payload);
  return response.data;
};

export const createNewsTag = async (payload: {
  name: string;
  slug: string;
  is_active?: boolean;
  sort_order?: number;
}): Promise<LocationDictionaryItem> => {
  const response = await apiClient.post<LocationDictionaryItem>('/v1/news-tags/', payload);
  return response.data;
};

export const createEventType = async (payload: {
  name: string;
  slug: string;
  is_active?: boolean;
  sort_order?: number;
}): Promise<LocationDictionaryItem> => {
  const response = await apiClient.post<LocationDictionaryItem>('/v1/event-types/', payload);
  return response.data;
};

export const listEventTypes = async (params?: {
  search?: string;
  ordering?: string;
  is_active?: boolean;
}): Promise<NormalizedListResponse<EventTypeItem>> => {
  const response = await apiClient.get<ApiListResponse<EventTypeItem>>('/v1/event-types/', { params });
  return normalizeListResponse(response.data);
};

export const listRoutes = async (params?: Record<string, string | number | boolean | undefined>) => {
  const response = await apiClient.get<ApiListResponse<RouteItem>>('/v1/routes/', { params });
  return normalizeListResponse(response.data);
};

export const listRouteModerationQueue = async (
  params?: PaginationQuery
): Promise<NormalizedListResponse<RouteItem>> => {
  const response = await apiClient.get<ApiListResponse<RouteItem>>('/v1/routes/moderation/queue/', { params });
  return normalizeListResponse(response.data);
};

export const getRoute = async (id: number): Promise<RouteItem> => {
  const response = await apiClient.get<RouteItem>(`/v1/routes/${id}/`);
  return response.data;
};

export const createRoute = async (
  payload: {
    title: string;
    waypoints: RouteWaypointItem[];
  } & Partial<RouteItem>
): Promise<RouteItem> => {
  const response = await apiClient.post<RouteItem>('/v1/routes/', sanitizeContentBlocksInPayload(payload as Record<string, unknown>));
  return response.data;
};

export const importRouteFromJson = async (payload: Record<string, unknown>): Promise<RouteItem> => {
  const response = await apiClient.post<RouteItem>('/v1/routes/import-json/', payload);
  return response.data;
};

export const importRouteIntoExistingFromJson = async (id: number, payload: Record<string, unknown>): Promise<RouteItem> => {
  const response = await apiClient.post<RouteItem>(`/v1/routes/${id}/import-json/`, payload);
  return response.data;
};

export const importEventFromJson = async (payload: Record<string, unknown>): Promise<EventItem> => {
  const response = await apiClient.post<EventItem>('/v1/events/import-json/', payload);
  return response.data;
};

export const importEventIntoExistingFromJson = async (id: number, payload: Record<string, unknown>): Promise<EventItem> => {
  const response = await apiClient.post<EventItem>(`/v1/events/${id}/import-json/`, payload);
  return response.data;
};

export const patchRoute = async (id: number, payload: Record<string, unknown>): Promise<RouteItem> => {
  const response = await apiClient.patch<RouteItem>(`/v1/routes/${id}/`, sanitizeContentBlocksInPayload(payload));
  return response.data;
};

export const publishRoute = async (id: number, comment = ''): Promise<RouteItem> => {
  const response = await apiClient.post<RouteItem>(`/v1/routes/${id}/publish/`, { comment });
  return response.data;
};

export const approveRoute = async (id: number, comment = ''): Promise<RouteItem> => {
  const response = await apiClient.post<RouteItem>(`/v1/routes/${id}/approve/`, { comment });
  return response.data;
};

export const rejectRoute = async (id: number, comment = ''): Promise<RouteItem> => {
  const response = await apiClient.post<RouteItem>(`/v1/routes/${id}/reject/`, { comment });
  return response.data;
};

export const deleteRoute = async (id: number): Promise<void> => {
  await apiClient.delete(`/v1/routes/${id}/`);
};

export const setRouteCover = async (
  id: number,
  payload: { coverImage: File } | { mediaId: number }
): Promise<RouteItem> => {
  const formData = new FormData();
  if ('coverImage' in payload) {
    formData.set('cover_image', payload.coverImage);
  } else {
    formData.set('media_id', String(payload.mediaId));
  }
  const response = await apiClient.patch<RouteItem>(`/v1/routes/${id}/cover/`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

export const listRouteMedia = async (
  id: number,
  params?: PaginationQuery
): Promise<NormalizedListResponse<RouteMediaItem>> => {
  const response = await apiClient.get<ApiListResponse<RouteMediaItem>>(`/v1/routes/${id}/media/`, { params });
  return normalizeListResponse(response.data);
};

export const listAllRouteMedia = async (id: number): Promise<NormalizedListResponse<RouteMediaItem>> =>
  collectAllPages<RouteMediaItem>((params) => listRouteMedia(id, params));

export const uploadRouteMedia = async (
  id: number,
  payload: {
    file: File;
    title?: string;
    sort_order?: number;
  }
): Promise<RouteMediaItem> => {
  const formData = new FormData();
  formData.set('file', payload.file);
  if (payload.title) formData.set('title', payload.title);
  if (payload.sort_order !== undefined) formData.set('sort_order', String(payload.sort_order));
  const response = await apiClient.post<RouteMediaItem>(`/v1/routes/${id}/media/`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

export const deleteRouteMedia = async (routeId: number, mediaId: number): Promise<void> => {
  await apiClient.delete(`/v1/routes/${routeId}/media/${mediaId}/`);
};

export const listEvents = async (params?: Record<string, string | number | boolean | undefined>) => {
  const response = await apiClient.get<ApiListResponse<EventItem>>('/v1/events/', { params });
  return normalizeListResponse(response.data);
};

export const getEvent = async (id: number): Promise<EventItem> => {
  const response = await apiClient.get<EventItem>(`/v1/events/${id}/`);
  return response.data;
};

export const listNews = async (params?: Record<string, string | number | boolean | undefined>) => {
  const response = await apiClient.get<ApiListResponse<NewsItem>>('/v1/news/', { params });
  return normalizeListResponse(response.data);
};

export const getDashboardModeration = async (): Promise<DashboardModerationSummary> => {
  const response = await apiClient.get<DashboardModerationSummary>('/v1/dashboard/moderation/');
  return response.data;
};

export const listDashboardMessageStream = async (params?: {
  module?: 'location_review' | 'route_review' | 'news_review' | 'event_review';
  search?: string;
  page?: number;
  page_size?: number;
}): Promise<NormalizedListResponse<DashboardMessageStreamItem>> => {
  const response = await apiClient.get<ApiListResponse<DashboardMessageStreamItem>>('/v1/dashboard/moderation/messages/', {
    params,
  });
  return normalizeListResponse(response.data);
};

export const deleteDashboardMessageStreamItem = async (
  module: 'location_review' | 'route_review' | 'news_review' | 'event_review',
  messageId: number
): Promise<void> => {
  await apiClient.post(`/v1/dashboard/moderation/messages/${module}/${messageId}/delete/`, {});
};

export const blockUserFromDashboardMessageStream = async (userId: number): Promise<void> => {
  await apiClient.post(`/v1/dashboard/moderation/users/${userId}/block/`, {});
};

export const getNews = async (id: number): Promise<NewsItem> => {
  const response = await apiClient.get<NewsItem>(`/v1/news/${id}/`);
  return response.data;
};

export const createNews = async (payload: Partial<NewsItem> & { title: string; category: number }): Promise<NewsItem> => {
  const response = await apiClient.post<NewsItem>('/v1/news/', sanitizeContentBlocksInPayload(payload as Record<string, unknown>));
  return response.data;
};

export const importNewsFromJson = async (payload: Record<string, unknown>): Promise<NewsItem> => {
  const response = await apiClient.post<NewsItem>('/v1/news/import-json/', payload);
  return response.data;
};

export const importNewsIntoExistingFromJson = async (id: number, payload: Record<string, unknown>): Promise<NewsItem> => {
  const response = await apiClient.post<NewsItem>(`/v1/news/${id}/import-json/`, payload);
  return response.data;
};

export const patchNews = async (id: number, payload: Record<string, unknown>): Promise<NewsItem> => {
  const response = await apiClient.patch<NewsItem>(`/v1/news/${id}/`, sanitizeContentBlocksInPayload(payload));
  return response.data;
};

export const publishNews = async (id: number, comment = ''): Promise<NewsItem> => {
  const response = await apiClient.post<NewsItem>(`/v1/news/${id}/publish/`, { comment });
  return response.data;
};

export const archiveNews = async (id: number, comment = ''): Promise<NewsItem> => {
  const response = await apiClient.post<NewsItem>(`/v1/news/${id}/archive/`, { comment });
  return response.data;
};

export const unpublishNews = async (id: number, comment = ''): Promise<NewsItem> => {
  const response = await apiClient.post<NewsItem>(`/v1/news/${id}/unpublish/`, { comment });
  return response.data;
};

export const deleteNews = async (id: number): Promise<void> => {
  await apiClient.delete(`/v1/news/${id}/`);
};

export const setNewsCover = async (id: number, coverImage: File): Promise<NewsItem> => {
  const formData = new FormData();
  formData.set('cover_image', coverImage);
  const response = await apiClient.patch<NewsItem>(`/v1/news/${id}/cover/`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

export const uploadNewsMedia = async (
  id: number,
  payload: {
    file: File;
    title?: string;
    sort_order?: number;
  }
): Promise<NewsMediaItem> => {
  const formData = new FormData();
  formData.set('file', payload.file);
  if (payload.title) formData.set('title', payload.title);
  if (payload.sort_order !== undefined) formData.set('sort_order', String(payload.sort_order));
  const response = await apiClient.post<NewsMediaItem>(`/v1/news/${id}/media/`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

export const listNewsMedia = async (
  id: number,
  params?: PaginationQuery
): Promise<NormalizedListResponse<NewsMediaItem>> => {
  const response = await apiClient.get<ApiListResponse<NewsMediaItem>>(`/v1/news/${id}/media/`, { params });
  return normalizeListResponse(response.data);
};

export const listAllNewsMedia = async (id: number): Promise<NormalizedListResponse<NewsMediaItem>> =>
  collectAllPages<NewsMediaItem>((params) => listNewsMedia(id, params));

export const deleteNewsMedia = async (id: number, mediaId: number): Promise<void> => {
  await apiClient.delete(`/v1/news/${id}/media/${mediaId}/`);
};

export const listNewsComments = async (
  newsId: number,
  params?: PaginationQuery
): Promise<NormalizedListResponse<NewsCommentItem>> => {
  const response = await apiClient.get<ApiListResponse<NewsCommentItem>>(`/v1/news/${newsId}/comments/`, { params });
  return normalizeListResponse(response.data);
};

export const listAllNewsComments = async (newsId: number): Promise<NormalizedListResponse<NewsCommentItem>> =>
  collectAllPages<NewsCommentItem>((params) => listNewsComments(newsId, params));

export const createNewsComment = async (newsId: number, message: string): Promise<NewsCommentItem> => {
  const response = await apiClient.post<NewsCommentItem>(`/v1/news/${newsId}/comments/`, { message });
  return response.data;
};

export const createEvent = async (
  payload: Partial<EventItem> & {
    title: string;
    starts_at: string;
    timezone: string;
  }
): Promise<EventItem> => {
  const response = await apiClient.post<EventItem>('/v1/events/', sanitizeContentBlocksInPayload(payload as Record<string, unknown>));
  return response.data;
};

export const patchEvent = async (id: number, payload: Record<string, unknown>): Promise<EventItem> => {
  const response = await apiClient.patch<EventItem>(`/v1/events/${id}/`, sanitizeContentBlocksInPayload(payload));
  return response.data;
};

export const publishEvent = async (id: number, comment = ''): Promise<EventItem> => {
  const response = await apiClient.post<EventItem>(`/v1/events/${id}/publish/`, { comment });
  return response.data;
};

export const cancelEvent = async (id: number, comment = ''): Promise<EventItem> => {
  const response = await apiClient.post<EventItem>(`/v1/events/${id}/cancel/`, { comment });
  return response.data;
};

export const finishEvent = async (id: number, comment = ''): Promise<EventItem> => {
  const response = await apiClient.post<EventItem>(`/v1/events/${id}/finish/`, { comment });
  return response.data;
};

export const deleteEvent = async (id: number): Promise<void> => {
  await apiClient.delete(`/v1/events/${id}/`);
};

export const setEventCover = async (id: number, coverImage: File): Promise<EventItem> => {
  const formData = new FormData();
  formData.set('cover_image', coverImage);
  const response = await apiClient.patch<EventItem>(`/v1/events/${id}/cover/`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

export const listEventMedia = async (
  id: number,
  params?: PaginationQuery
): Promise<NormalizedListResponse<EventMediaItem>> => {
  const response = await apiClient.get<ApiListResponse<EventMediaItem>>(`/v1/events/${id}/media/`, { params });
  return normalizeListResponse(response.data);
};

export const listAllEventMedia = async (id: number): Promise<NormalizedListResponse<EventMediaItem>> =>
  collectAllPages<EventMediaItem>((params) => listEventMedia(id, params));

export const uploadEventMedia = async (
  id: number,
  payload: {
    file: File;
    title?: string;
    sort_order?: number;
  }
): Promise<EventMediaItem> => {
  const formData = new FormData();
  formData.set('file', payload.file);
  if (payload.title) formData.set('title', payload.title);
  if (payload.sort_order !== undefined) formData.set('sort_order', String(payload.sort_order));
  const response = await apiClient.post<EventMediaItem>(`/v1/events/${id}/media/`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

export const deleteEventMedia = async (eventId: number, mediaId: number): Promise<void> => {
  await apiClient.delete(`/v1/events/${eventId}/media/${mediaId}/`);
};

export const listEventMessages = async (
  eventId: number,
  params?: PaginationQuery
): Promise<NormalizedListResponse<EventMessageItem>> => {
  const response = await apiClient.get<ApiListResponse<EventMessageItem>>(`/v1/events/${eventId}/messages/`, { params });
  return normalizeListResponse(response.data);
};

export const listAllEventMessages = async (eventId: number): Promise<NormalizedListResponse<EventMessageItem>> =>
  collectAllPages<EventMessageItem>((params) => listEventMessages(eventId, params));

export const createEventMessage = async (eventId: number, message: string): Promise<EventMessageItem> => {
  const response = await apiClient.post<EventMessageItem>(`/v1/events/${eventId}/messages/`, { message });
  return response.data;
};

export const listRouteReviews = async (
  routeId: number,
  params?: PaginationQuery
): Promise<RouteReviewListResponse> => {
  const response = await apiClient.get<
    | ApiListResponse<RouteReview>
    | ({ summary?: RouteReviewsSummary } & {
        count?: number;
        next?: string | null;
        previous?: string | null;
        results?: RouteReview[];
      })
  >(`/v1/routes/${routeId}/reviews/`, { params });
  const payload = response.data;
  const normalized = normalizeListResponse<RouteReview>(payload as ApiListResponse<RouteReview>);
  const summary =
    Array.isArray(payload) || !('summary' in payload)
      ? createEmptyRouteReviewsSummary()
      : payload.summary ?? createEmptyRouteReviewsSummary();
  return {
    ...normalized,
    summary,
  };
};

export const listAllRouteReviews = async (routeId: number): Promise<RouteReviewListResponse> => {
  let page = 1;
  let pagesRead = 0;
  let count = 0;
  let previous: string | null = null;
  let next: string | null = null;
  let summary = createEmptyRouteReviewsSummary();
  const results: RouteReview[] = [];

  while (page <= BULK_MAX_PAGES) {
    const payload = await listRouteReviews(routeId, { page, page_size: BULK_PAGE_SIZE });
    if (page === 1) {
      count = payload.count;
      previous = payload.previous;
      summary = payload.summary;
    }
    results.push(...payload.results);
    next = payload.next;
    pagesRead += 1;
    if (!payload.next || payload.results.length === 0) break;
    page += 1;
  }

  if (pagesRead === BULK_MAX_PAGES && next) {
    next = null;
  }

  return {
    count: Math.max(count, results.length),
    next,
    previous,
    results,
    summary,
  };
};

export const createRouteReview = async (
  routeId: number,
  payload: { message: string; rating_value: number }
): Promise<RouteReview> => {
  const response = await apiClient.post<RouteReview>(`/v1/routes/${routeId}/reviews/`, payload);
  return response.data;
};

export const deleteRouteReview = async (routeId: number, reviewId: number): Promise<void> => {
  await apiClient.delete(`/v1/routes/${routeId}/reviews/${reviewId}/`);
};

export const rateRoute = async (routeId: number, value: number): Promise<RouteRatingSummaryResponse> => {
  const response = await apiClient.post<RouteRatingSummaryResponse>(`/v1/routes/${routeId}/ratings/`, { value });
  return response.data;
};

export const getMyRouteRating = async (routeId: number): Promise<RouteMyRating | null> => {
  try {
    const response = await apiClient.get<RouteMyRating>(`/v1/routes/${routeId}/ratings/my/`);
    return response.data;
  } catch (error) {
    const axiosError = error as AxiosError;
    if (axiosError.response?.status === 204) {
      return null;
    }
    throw error;
  }
};

export const generateLocationDescriptionTask = async (payload: {
  title: string;
  requirements?: string;
  latitude?: number;
  longitude?: number;
}): Promise<AiTask> => {
  const response = await apiClient.post<AiTask>('/v1/ai/tasks/locations/generate-description/', payload);
  return response.data;
};

export const getAiTask = async (id: number): Promise<AiTask> => {
  const response = await apiClient.get<AiTask>(`/v1/ai/tasks/${id}/`);
  return response.data;
};

export const listAiProposals = async (params?: {
  task?: number;
  status?: 'draft' | 'approved' | 'rejected' | 'applied';
  page?: number;
  page_size?: number;
}): Promise<NormalizedListResponse<AiProposal>> => {
  const response = await apiClient.get<ApiListResponse<AiProposal>>('/v1/ai/proposals/', { params });
  return normalizeListResponse(response.data);
};

export const approveAiProposal = async (id: number, comment = ''): Promise<AiProposal> => {
  const response = await apiClient.post<AiProposal>(`/v1/ai/proposals/${id}/approve/`, { comment });
  return response.data;
};

export const rejectAiProposal = async (id: number, comment = ''): Promise<AiProposal> => {
  const response = await apiClient.post<AiProposal>(`/v1/ai/proposals/${id}/reject/`, { comment });
  return response.data;
};

export const applyAiProposal = async (id: number): Promise<AiProposal> => {
  const response = await apiClient.post<AiProposal>(`/v1/ai/proposals/${id}/apply/`, {});
  return response.data;
};
